"use client";

import {
  Container,
  Title,
  Text,
  Box,
  Stack,
  SimpleGrid,
  Group,
  Button,
  Anchor,
  Divider,
  Paper,
  ThemeIcon,
} from "@mantine/core";
import { IconArrowRight, IconCheck, IconX } from "@tabler/icons-react";
import Link from "next/link";

const topColors = [
  { color: "#FF7F50", name: "Coral", why: "Vibrant and warm—pops beautifully against tan skin." },
  { color: "#40E0D0", name: "Turquoise", why: "Stunning contrast that works for most undertones." },
  { color: "#FFFFFF", name: "Bright White", why: "Creates clean, fresh contrast with medium skin." },
  { color: "#4169E1", name: "Royal Blue", why: "Bold and flattering across warm and cool tans." },
  { color: "#228B22", name: "Emerald Green", why: "Rich jewel tone that makes tan skin glow." },
  { color: "#FFD700", name: "Gold", why: "Warm metallic that enhances golden undertones." },
  { color: "#DC143C", name: "Crimson", why: "Bold red that works on both warm and cool tans." },
  { color: "#FF69B4", name: "Hot Pink", why: "Fun, bright, and surprisingly flattering." },
  { color: "#C71585", name: "Berry", why: "Deep pink-purple that adds richness." },
  { color: "#9400D3", name: "Violet", why: "Vibrant purple that creates beautiful contrast." },
  { color: "#20B2AA", name: "Light Sea Green", why: "Fresh aqua tone perfect for summer." },
  { color: "#FFA07A", name: "Light Salmon", why: "Soft, warm, and naturally flattering." },
];

const warmTanColors = [
  { color: "#FF7F50", name: "Coral" },
  { color: "#FFD700", name: "Gold" },
  { color: "#40E0D0", name: "Turquoise" },
  { color: "#FF6347", name: "Tomato Red" },
  { color: "#32CD32", name: "Lime Green" },
];

const coolTanColors = [
  { color: "#4169E1", name: "Royal Blue" },
  { color: "#C71585", name: "Berry" },
  { color: "#228B22", name: "Emerald" },
  { color: "#9400D3", name: "Violet" },
  { color: "#FFFFFF", name: "Bright White" },
];

const neutralTanColors = [
  { color: "#008080", name: "Teal" },
  { color: "#DC143C", name: "Crimson" },
  { color: "#20B2AA", name: "Sea Green" },
  { color: "#FF69B4", name: "Hot Pink" },
  { color: "#FFA07A", name: "Light Salmon" },
];

const colorsToAvoid = [
  { color: "#D2B48C", name: "Tan/Beige", why: "Too close to your skin tone—looks washed out", instead: "Bright white or coral" },
  { color: "#696969", name: "Muddy Gray", why: "Lacks vibrancy—makes skin look dull", instead: "Charcoal or teal" },
  { color: "#BDB76B", name: "Dark Khaki", why: "Blends in and lacks contrast", instead: "Emerald or turquoise" },
  { color: "#C4AEAD", name: "Dusty Pink", why: "Too muted for medium skin tones", instead: "Coral or hot pink" },
  { color: "#808080", name: "Medium Gray", why: "Flat and unflattering on most tans", instead: "Navy or charcoal" },
];

function ColorSwatch({ color, size = 48 }: { color: string; size?: number }) {
  return (
    <Box
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        borderRadius: 8,
        border: color === "#FFFFFF" ? "1px solid #e0e0e0" : "none",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    />
  );
}

export default function BestColorsForTanSkinContent() {
  return (
    <Container size="md" py="xl">
      {/* HERO */}
      <Stack gap="xs" mb={48}>
        <Title order={1} size={36} lh={1.2}>
          Best Colors for Tan Skin
        </Title>
        <Text size="xl" c="dimmed" maw={600}>
          Bright, saturated colors that make medium skin tones look incredible.
        </Text>
      </Stack>

      {/* SECTION 1: TOP 12 COLORS */}
      <Box mb={64}>
        <Title order={2} size={28} mb={32}>
          Top 12 Best Colors for Tan Skin
        </Title>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24}>
          {topColors.map((item) => (
            <Group key={item.name} gap="md" align="flex-start">
              <ColorSwatch color={item.color} size={56} />
              <Box style={{ flex: 1 }}>
                <Text fw={600} size="lg">{item.name}</Text>
                <Text c="dimmed" size="sm">{item.why}</Text>
              </Box>
            </Group>
          ))}
        </SimpleGrid>

        <Paper withBorder p="lg" mt={40} radius="md" bg="gray.0">
          <Text size="md" mb="sm">
            Not sure which of these is <em>your</em> best color?
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            color="teal"
            rightSection={<IconArrowRight size={16} />}
          >
            Get your exact palette with AI Color Analysis
          </Button>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 2: VISUAL PROOF */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          What These Colors Look Like on Tan Skin
        </Title>

        <Text c="dimmed" mb={32} maw={700}>
          Tan skin is versatile—you can wear bold, saturated colors that might overwhelm paler skin or get lost on deeper tones. The key is choosing colors with enough vibrancy.
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24} mb={32}>
          <Paper withBorder p="lg" radius="md">
            <Group gap="xs" mb="md">
              <ThemeIcon color="green" size="sm" radius="xl">
                <IconCheck size={14} />
              </ThemeIcon>
              <Text fw={600}>Flattering</Text>
            </Group>
            <Group gap="sm" mb="md">
              <ColorSwatch color="#FF7F50" size={40} />
              <ColorSwatch color="#40E0D0" size={40} />
              <ColorSwatch color="#4169E1" size={40} />
              <ColorSwatch color="#FFFFFF" size={40} />
            </Group>
            <Text size="sm" c="dimmed">
              Bright, saturated colors create beautiful contrast with tan skin.
            </Text>
          </Paper>

          <Paper withBorder p="lg" radius="md">
            <Group gap="xs" mb="md">
              <ThemeIcon color="red" size="sm" radius="xl">
                <IconX size={14} />
              </ThemeIcon>
              <Text fw={600}>Unflattering</Text>
            </Group>
            <Group gap="sm" mb="md">
              <ColorSwatch color="#D2B48C" size={40} />
              <ColorSwatch color="#696969" size={40} />
              <ColorSwatch color="#BDB76B" size={40} />
              <ColorSwatch color="#C4AEAD" size={40} />
            </Group>
            <Text size="sm" c="dimmed">
              Muted, dusty, or skin-tone colors blend in and look flat.
            </Text>
          </Paper>
        </SimpleGrid>

        <Paper p="lg" radius="md" bg="blue.0">
          <Text fw={600} mb="xs">Why tan skin is versatile:</Text>
          <Text size="sm">
            Medium skin tones hit a sweet spot—you have enough depth to handle bold colors without being overwhelmed, but enough contrast with white and bright tones to make them pop. Lean into saturation and brightness.
          </Text>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 3: BY UNDERTONE */}
      <Box mb={64}>
        <Title order={2} size={28} mb={32}>
          Best Colors for Tan Skin by Undertone
        </Title>

        <Stack gap={40}>
          {/* Warm */}
          <Box>
            <Title order={3} size={22} mb={16}>
              Warm Undertones
            </Title>
            <Text size="sm" c="dimmed" mb={16}>Golden, peachy, or yellow undertones. Gold jewelry looks best.</Text>

            <Group gap="lg" mb={16}>
              {warmTanColors.map((item) => (
                <Stack key={item.name} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{item.name}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              <strong>Why they work:</strong> Warm colors enhance the golden glow in your skin, creating a sun-kissed, radiant effect.
            </Text>
          </Box>

          {/* Cool */}
          <Box>
            <Title order={3} size={22} mb={16}>
              Cool Undertones
            </Title>
            <Text size="sm" c="dimmed" mb={16}>Pink, red, or bluish undertones. Silver jewelry looks best.</Text>

            <Group gap="lg" mb={16}>
              {coolTanColors.map((item) => (
                <Stack key={item.name} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{item.name}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              <strong>Why they work:</strong> Cool tones complement the pink undertones in your skin, adding brightness and freshness.
            </Text>
          </Box>

          {/* Neutral */}
          <Box>
            <Title order={3} size={22} mb={16}>
              Neutral Undertones
            </Title>
            <Text size="sm" c="dimmed" mb={16}>Mix of warm and cool. Both gold and silver jewelry work.</Text>

            <Group gap="lg" mb={16}>
              {neutralTanColors.map((item) => (
                <Stack key={item.name} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{item.name}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              <strong>Why they work:</strong> Neutral undertones have flexibility—you can pull off both warm and cool colors, as long as they're vibrant.
            </Text>
          </Box>
        </Stack>

        <Paper withBorder p="lg" mt={40} radius="md" bg="teal.0">
          <Text fw={600} mb="sm">
            Most people guess their undertone wrong.
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            color="teal"
            size="md"
            rightSection={<IconArrowRight size={16} />}
          >
            Find your exact undertone + best colors instantly
          </Button>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 4: COLORS TO AVOID */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          Colors That Make Tan Skin Look Dull
        </Title>

        <Text c="dimmed" mb={32}>
          Some colors lack the vibrancy needed to complement medium skin tones.
        </Text>

        <Stack gap={20}>
          {colorsToAvoid.map((item) => (
            <Group key={item.name} gap="md" align="flex-start">
              <ColorSwatch color={item.color} size={48} />
              <Box style={{ flex: 1 }}>
                <Group gap="xs">
                  <Text fw={600}>{item.name}</Text>
                  <Text c="red.6" size="sm">— {item.why}</Text>
                </Group>
                <Text size="sm" c="teal.7">
                  Instead, choose: <strong>{item.instead}</strong>
                </Text>
              </Box>
            </Group>
          ))}
        </Stack>
      </Box>

      <Divider my={48} />

      {/* SECTION 5: WHITE */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          White Looks Great on Tan Skin
        </Title>

        <Text c="dimmed" mb={24}>
          One of the best colors for tan skin—period.
        </Text>

        <Paper p="lg" radius="md" bg="gray.1" mb={24}>
          <Text size="lg" ta="center">
            White creates beautiful contrast with tan skin, making you look fresh, clean, and radiant. It's a summer staple for a reason.
          </Text>
        </Paper>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={16}>
          <Paper withBorder p="md" radius="md">
            <Text fw={600} mb="xs">Bright White</Text>
            <Text size="sm" c="dimmed">
              Crisp and clean. Works especially well on cool-toned tan skin.
            </Text>
          </Paper>
          <Paper withBorder p="md" radius="md">
            <Text fw={600} mb="xs">Cream/Off-White</Text>
            <Text size="sm" c="dimmed">
              Softer and warmer. Beautiful on warm-toned tan skin.
            </Text>
          </Paper>
        </SimpleGrid>
      </Box>

      <Divider my={48} />

      {/* SECTION 6: SEASONAL */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          Your Exact Color Season Matters
        </Title>

        <Text c="dimmed" mb={8}>
          Here's the thing:
        </Text>

        <Paper p="md" radius="md" bg="gray.1" mb={24}>
          <Text fw={600} size="lg" ta="center">
            Not all tan skin is the same.
          </Text>
        </Paper>

        <Text mb={32}>
          Two people with tan skin can look completely different in the same color. Your <strong>color season</strong> determines your exact best palette.
        </Text>

        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={16} mb={32}>
          <Anchor component={Link} href="/color-palette/spring/true" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#FF7F50" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#40E0D0" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#FFEFD5" style={{ borderRadius: 4, border: "1px solid #ddd" }} />
              </Group>
              <Text fw={600} size="sm">True Spring</Text>
              <Text size="xs" c="dimmed">Warm & bright</Text>
            </Paper>
          </Anchor>

          <Anchor component={Link} href="/color-palette/summer/true" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#6B8E9B" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#C8A4D4" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#B0C4DE" style={{ borderRadius: 4 }} />
              </Group>
              <Text fw={600} size="sm">True Summer</Text>
              <Text size="xs" c="dimmed">Cool & soft</Text>
            </Paper>
          </Anchor>

          <Anchor component={Link} href="/color-palette/autumn/true" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#CD853F" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#228B22" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#8B4513" style={{ borderRadius: 4 }} />
              </Group>
              <Text fw={600} size="sm">True Autumn</Text>
              <Text size="xs" c="dimmed">Warm & rich</Text>
            </Paper>
          </Anchor>

          <Anchor component={Link} href="/color-palette/winter/cool" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#FF1493" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#00BFFF" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#FFFFFF" style={{ borderRadius: 4, border: "1px solid #ddd" }} />
              </Group>
              <Text fw={600} size="sm">Bright Winter</Text>
              <Text size="xs" c="dimmed">Cool & vivid</Text>
            </Paper>
          </Anchor>
        </SimpleGrid>

        <Paper p="xl" radius="md" bg="teal.6" c="white" ta="center">
          <Text size="lg" fw={600} mb="sm">
            Get your personalized color palette
          </Text>
          <Text size="sm" mb="md" opacity={0.9}>
            Takes 30 seconds. See your exact best colors.
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            color="teal"
            variant="white"
            size="lg"
            rightSection={<IconArrowRight size={18} />}
          >
            Start Free Analysis
          </Button>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 7: FAQ */}
      <Box mb={48}>
        <Title order={2} size={28} mb={32}>
          Frequently Asked Questions
        </Title>

        <Stack gap={24}>
          <Box>
            <Text fw={600} mb={4}>What colors look best on tan skin?</Text>
            <Text c="dimmed">
              Bright, saturated colors like coral, turquoise, royal blue, emerald, and bright white. Tan skin can handle bold colors that create beautiful contrast.
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>Does white look good on tan skin?</Text>
            <Text c="dimmed">
              Yes! White is one of the best colors for tan skin. It creates fresh, clean contrast and makes skin look radiant.
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>What colors should tan skin avoid?</Text>
            <Text c="dimmed">
              Muted, dusty colors like beige, khaki, and muddy grays. Also avoid colors too close to your skin tone—they blend in and look flat.
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>Is tan skin warm or cool toned?</Text>
            <Text c="dimmed">
              Tan skin can be either. Check your wrist veins: green = warm, blue/purple = cool. The jewelry test helps too: does gold or silver look better on you?
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>What hair color looks best on tan skin?</Text>
            <Text c="dimmed">
              Warm undertones: caramel, honey, warm brown, copper. Cool undertones: ash brown, cool espresso, burgundy. Both can rock rich chocolate brown.
            </Text>
          </Box>
        </Stack>
      </Box>
    </Container>
  );
}
